import {
  REQUEST_COMPLETED_STATUS,
  REQUEST_DENIED_STATUS,
  REQUEST_IN_PROGRESS_STATUS,
  REQUEST_PENDING_STATUS,
} from '../../constants/request'

export const BirdviewTrend = {
  Down: 'down',
  Flat: 'flat',
  Up: 'up',
}

export const BIRDVIEW_TREND_GLYPH = {
  [BirdviewTrend.Down]: '↓',
  [BirdviewTrend.Flat]: '→',
  [BirdviewTrend.Up]: '↑',
}

// The prototype's tone palette, not the status pill colors.
export const BirdviewChartTone = {
  Error: 'var(--color-error)',
  Info: 'var(--color-info)',
  Purple: 'var(--color-purple)',
  Success: 'var(--color-success)',
  Warning: 'var(--color-warning)',
}

export const BirdviewRequestChart = {
  Aging: 'aging',
  ByBuilding: 'byBuilding',
  ByManager: 'byManager',
  ByStatus: 'byStatus',
  ByType: 'byType',
  OpenedVsClosed: 'openedVsClosed',
}

// What the bars measure; the grouping stays (IU-954 `metric`).
export const BirdviewChartMetric = {
  AvgTime: 'avgTime',
  Count: 'count',
  PerUnits: 'per100',
}

export const BIRDVIEW_CHART_BUILDING_METRIC_OPTIONS = [
  {
    labelKey: 'DashboardBirdview.MetricCountKey',
    value: BirdviewChartMetric.Count,
  },
  {
    labelKey: 'DashboardBirdview.MetricPerUnitsKey',
    value: BirdviewChartMetric.PerUnits,
  },
  {
    labelKey: 'DashboardBirdview.MetricAvgTimeKey',
    value: BirdviewChartMetric.AvgTime,
  },
]

// Managers are not normalised by units (SR-07).
export const BIRDVIEW_CHART_MANAGER_METRIC_OPTIONS = [
  BIRDVIEW_CHART_BUILDING_METRIC_OPTIONS[0],
  BIRDVIEW_CHART_BUILDING_METRIC_OPTIONS[2],
]

// What the age of a request is counted from (IU-954 `since`).
export const BirdviewAgingBasis = {
  Activity: 'activity',
  Created: 'created',
}

export const BIRDVIEW_AGING_BASIS_OPTIONS = [
  {
    labelKey: 'DashboardBirdview.AgingBasisCreatedKey',
    value: BirdviewAgingBasis.Created,
  },
  {
    labelKey: 'DashboardBirdview.AgingBasisActivityKey',
    value: BirdviewAgingBasis.Activity,
  },
]

export const BirdviewChartAction = {
  Open: 'open',
  Period: 'period',
  Remove: 'remove',
  Size: 'size',
}

export const BIRDVIEW_CHART_ACTION_OPTIONS = [
  {
    labelKey: 'DashboardBirdview.ChartActionPeriodKey',
    value: BirdviewChartAction.Period,
  },
  {
    labelKey: 'DashboardBirdview.ChartActionSizeKey',
    value: BirdviewChartAction.Size,
  },
  {
    labelKey: 'DashboardBirdview.ChartActionOpenInModuleKey',
    value: BirdviewChartAction.Open,
  },
  {
    labelKey: 'DashboardBirdview.ChartActionRemoveKey',
    value: BirdviewChartAction.Remove,
  },
]

export const BIRDVIEW_CHART_UNITS_BASE = 100

export const BirdviewChartType = {
  Aging: 'aging',
  Bars: 'bars',
  Donut: 'donut',
  Line: 'line',
}

export const BirdviewChartObject = {
  Buildings: 'buildings',
  DocFlows: 'docFlows',
  Emails: 'emails',
  Requests: 'requests',
  Units: 'units',
}

export const BIRDVIEW_CHART_PERIOD_OPTIONS = [
  { days: 7, labelKey: 'DashboardBirdview.Period7DaysOptionKey', value: '7' },
  {
    days: 30,
    labelKey: 'DashboardBirdview.Period30DaysOptionKey',
    value: '30',
  },
  {
    days: 90,
    labelKey: 'DashboardBirdview.Period90DaysOptionKey',
    value: '90',
  },
]

export const BIRDVIEW_CHART_DEFAULT_PERIOD = BIRDVIEW_CHART_PERIOD_OPTIONS[1]

export const BIRDVIEW_CHART_DATE_FORMAT = 'dd/MM'

// The field a chart reads its x-axis name from: a date for series, a label for
// items.
export const BIRDVIEW_CHART_DATE_KEY = 'date'

export const BIRDVIEW_CHART_LABEL_KEY = 'label'

export const BIRDVIEW_CHART_FLOW_POINTS = 12

// The two segments of a stacked bar, in drawing order; `key` is the item field.
export const BIRDVIEW_CHART_STACKED_SEGMENTS = [
  {
    color: BirdviewChartTone.Info,
    key: 'open',
    labelKey: 'DashboardBirdview.ChartOpenLegendKey',
  },
  {
    color: BirdviewChartTone.Error,
    key: 'overdue',
    labelKey: 'DashboardBirdview.TileOverdueKey',
  },
]

export const BIRDVIEW_CHART_REQUEST_STATUS_SEGMENTS = [
  { color: BirdviewChartTone.Info, status: REQUEST_PENDING_STATUS },
  { color: BirdviewChartTone.Warning, status: REQUEST_IN_PROGRESS_STATUS },
  { color: BirdviewChartTone.Success, status: REQUEST_COMPLETED_STATUS },
  { color: BirdviewChartTone.Error, status: REQUEST_DENIED_STATUS },
]

// Lower bound inclusive, upper exclusive; requests younger than the first
// bucket have no column and count only in the section total.
export const BIRDVIEW_CHART_AGING_BUCKETS = [
  {
    apiKey: 'days7To13',
    color: BirdviewChartTone.Success,
    labelKey: 'DashboardBirdview.AttentionAgeRangeOptionKey',
    maxDays: 14,
    minDays: 7,
  },
  {
    apiKey: 'days14To29',
    color: BirdviewChartTone.Info,
    labelKey: 'DashboardBirdview.AttentionAgeRangeOptionKey',
    maxDays: 30,
    minDays: 14,
  },
  {
    apiKey: 'days30To89',
    color: BirdviewChartTone.Warning,
    labelKey: 'DashboardBirdview.AttentionAgeRangeOptionKey',
    maxDays: 90,
    minDays: 30,
  },
  {
    apiKey: 'days90Plus',
    color: BirdviewChartTone.Error,
    labelKey: 'DashboardBirdview.AttentionAgeFromOptionKey',
    minDays: 90,
  },
]

// The field names of a flow point.
export const BIRDVIEW_CHART_FLOW_OPENED_KEY = 'opened'

export const BIRDVIEW_CHART_FLOW_CLOSED_KEY = 'closed'

export const BIRDVIEW_CHART_FLOW_SERIES = [
  {
    color: BirdviewChartTone.Info,
    dataKey: BIRDVIEW_CHART_FLOW_OPENED_KEY,
    labelKey: 'DashboardBirdview.ChartOpenedLegendKey',
  },
  {
    color: BirdviewChartTone.Success,
    dataKey: BIRDVIEW_CHART_FLOW_CLOSED_KEY,
    labelKey: 'DashboardBirdview.ChartClosedLegendKey',
  },
]

export const BirdviewAnalyticsPreset = {
  Personal: 'personal',
  Portfolio: 'portfolio',
}

export const BirdviewKpiUnit = {
  Count: 'count',
  Days: 'days',
  Hours: 'hours',
  Percent: 'percent',
}

export const BIRDVIEW_KPI_SPARK_DAYS = 30
